import React from "react";
import { ArrowRight2 } from "iconsax-react";
import styled from "styled-components";

const ItemButton = styled.button<{ $danger?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  border-bottom: 1px solid var(--color-border);
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: color-mix(
      in srgb,
      var(--color-primary) 10%,
      var(--color-surface)
    );
  }

  .icon-box {
    width: 44px;
    height: 44px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--color-surface-secondary);
    color: ${({ $danger }) =>
      $danger ? "var(--color-danger)" : "var(--color-primary)"};
  }

  .text-box {
    flex: 1;
    min-width: 0;

    h5 {
      margin: 0;
      font-size: 19px;
      line-height: 32px;
      font-weight: var(--font-weight-semibold);
      color: ${({ $danger }) =>
        $danger ? "var(--color-danger)" : "var(--color-text-title)"};
    }

    span {
      display: block;
      margin-top: 2px;
      font-size: 14px;
      line-height: 24px;
      color: var(--color-text-secondary);
    }
  }

  .arrow {
    color: var(--color-text-tertiary);
    flex-shrink: 0;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
  }

  .toggle-text {
    flex: 1;
    min-width: 0;

    h5 {
      margin: 0;
      font-size: 19px;
      line-height: 32px;
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-title);
    }

    span {
      display: block;
      margin-top: 2px;
      font-size: 14px;
      line-height: 24px;
      color: var(--color-text-secondary);
    }
  }
`;

const Switch = styled.button<{ $on?: boolean }>`
  width: 52px;
  height: 32px;
  border-radius: 16px;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  background: ${({ $on, theme }) =>
    $on ? theme.colors.primary : theme.colors.border};
  position: relative;
  transition: background 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    top: 4px;
    left: ${({ $on }) => ($on ? "24px" : "4px")};
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

export type SettingsMenuItemProps = {
  title: string;
  description?: string;
  icon: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
};

export function SettingsMenuItem({
  title,
  description,
  icon,
  onClick,
  danger,
}: SettingsMenuItemProps) {
  return (
    <ItemButton type="button" onClick={onClick} $danger={danger}>
      <div className="icon-box">{icon}</div>
      <div className="text-box">
        <h5>{title}</h5>
        {description ? <span>{description}</span> : null}
      </div>
      <ArrowRight2 size={18} className="arrow" />
    </ItemButton>
  );
}

export type SettingsToggleItemProps = {
  title: string;
  description?: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
};

export function SettingsToggleItem({
  title,
  description,
  checked,
  onToggle,
  disabled,
}: SettingsToggleItemProps) {
  return (
    <ToggleRow>
      <div className="toggle-text">
        <h5>{title}</h5>
        {description ? <span>{description}</span> : null}
      </div>
      <Switch
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={title}
        $on={checked}
        disabled={disabled}
        onClick={disabled ? undefined : onToggle}
        style={disabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
      />
    </ToggleRow>
  );
}

export const SettingsSection = styled.section`
  margin-bottom: 24px;
`;

export const SettingsSectionTitle = styled.h3`
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-secondary);
  margin: 0 0 8px 4px;
`;

export const SettingsCard = styled.div`
  background: var(--color-surface);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

export const SettingsFooterNote = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text-tertiary);
  text-align: center;
  margin: 24px 16px 8px;
`;
